import _ from 'underscore';

import {STAGE_RUN_STATUS, STAGE_RUN_STATUS_VIRTUAL} from 'workflows/constants/StageRunConstants';
import {ExperimentalConditionValue} from 'workflows/WorkflowConstants';

import {StageFlowNodeGraph, StageRunGraph, TaggedNodeGraph} from './TaggedNodeGraph';

export const VIRTUAL_NODE_ID_PREFIX = 'virtual_node_';

interface Node {
  id: string;
  parentIds: Array<string>;
  stageIndex: number;
}

/**
 * Class representing a graph that is currently being configured, so that there may be new "virtual" nodes
 * and the existing nodes may have an edited list of parents (possibly containing new nodes).
 *
 * Objects should not be mutated, because that interferes with the memoization.
 */
export class VirtualNodeGraph<T extends StageRunGraph | StageFlowNodeGraph> extends TaggedNodeGraph {
  nodes: Array<Node>;
  originalGraph: T;
  /**
   * Generally the constructor should not be used directly; use createVirtualNodeGraph instead.
   */
  constructor(taggedNodeGraph: T, nodes: Array<Node>) {
    super();
    // Each node is of the shape {id, parentIds, stageIndex}
    // and may represent an existing or a new node.
    // It is a light-weight representation of the existing nodes in taggedNodeGraph, and is the source
    // of truth for what will be created upon confirmation.
    this.nodes = nodes;
    this.originalGraph = taggedNodeGraph;
  }

  // Override superclass method
  override getNodeIds(): Array<string> {
    // eslint-disable-next-line no-restricted-syntax
    return _.pluck(this.nodes, 'id');
  }

  getNode(nodeId: string): Node {
    return _.findWhere(this.nodes, {id: nodeId})!;
  }

  // Override superclass method
  override getParentNodeIds(nodeId: string): Array<string> {
    return this.getNode(nodeId).parentIds;
  }

  // Override superclass method
  override getTag(nodeId: string): number {
    return this.getNode(nodeId).stageIndex;
  }

  // Override superclass method
  override getTags(): Array<number> {
    return this.originalGraph.getTags();
  }

  getOriginalNodeIds(): Array<string> {
    return this.originalGraph.getNodeIds();
  }

  getOriginalParentNodeIds(nodeId: string): Array<string> {
    return this.originalGraph.getParentNodeIds(nodeId);
  }

  getVirtualNodeIds(): Array<string> {
    // eslint-disable-next-line no-restricted-syntax
    return _.pluck(
      this.nodes.filter((node) => !this.originalGraph.getNodeIds().includes(node.id)),
      'id'
    );
  }

  isVirtual(nodeId: string): boolean {
    return this.getVirtualNodeIds().includes(nodeId);
  }

  /**
   * Temporary hack (T11704): will be moved to React component state when it is editable in the modal table.
   */
  getExperimentalConditionValues(nodeId: string): Array<ExperimentalConditionValue> {
    if (this.isVirtual(nodeId)) {
      return [];
    } else {
      return this.originalGraph.getExperimentalConditionValues(nodeId);
    }
  }

  getName(nodeId: string): string | null {
    if (this.isVirtual(nodeId)) {
      return null;
    } else {
      return this.originalGraph.getName(nodeId);
    }
  }

  getStatus(nodeId: string): STAGE_RUN_STATUS | typeof STAGE_RUN_STATUS_VIRTUAL | null {
    if (this.isVirtual(nodeId)) {
      return STAGE_RUN_STATUS_VIRTUAL;
    } else {
      return this.originalGraph.getStatus(nodeId);
    }
  }

  /**
   * Return a new VirtualNodeGraph where nodeId has parents parentNodeIds.
   */
  setParentNodeIds(nodeId: string, parentNodeIds: Array<string>): VirtualNodeGraph<T> {
    const newNodes = this.nodes.map((node) => {
      if (node.id === nodeId) {
        return {
          ...node,
          parentIds: parentNodeIds,
        };
      } else {
        return node;
      }
    });
    return new VirtualNodeGraph(this.originalGraph, newNodes);
  }

  addVirtualNodes(virtualNodes: Array<Node>): VirtualNodeGraph<T> {
    return new VirtualNodeGraph(this.originalGraph, [...this.nodes, ...virtualNodes]);
  }

  /**
   * Create a new VirtualNodeGraph by placing virtual nodes on a set of links.
   *
   * Each link is of the shape {parentId, childId, stageIndex} where both parentId and childId
   * are nullable and refer to nodes already in this graph. The stageIndex is the index of the stage of
   * the new node.
   */
  addNodesOnLinks(
    links: Array<{parentId: string | null; childId: string | null; stageIndex: number}>
  ): VirtualNodeGraph<T> {
    const nodesToAdd = links.map((link) => ({
      id: _.uniqueId(VIRTUAL_NODE_ID_PREFIX),
      parentIds: link.parentId ? [link.parentId] : [],
      stageIndex: link.stageIndex,
      link, // Only for use in this function
    }));

    // Return the ID of the new node that will be the parent of the child
    const replaceParentId = (parentId: string, childId: string): string => {
      const relevantLink = _.findWhere(links, {parentId, childId});
      if (relevantLink) {
        return _.findWhere(nodesToAdd, {link: relevantLink})!.id;
      } else {
        return parentId;
      }
    };

    // Return an array possibly containing the ID of a new node that will be the parent of an initial node
    const newParentIds = (childId: string): Array<string> => {
      const relevantLink = _.findWhere(links, {parentId: null, childId});
      if (relevantLink) {
        return [_.findWhere(nodesToAdd, {link: relevantLink})!.id];
      } else {
        return [];
      }
    };

    const nodes = this.nodes.map((node) => ({
      ...node,
      parentIds: [
        ...node.parentIds.map((parentId) => replaceParentId(parentId, node.id)),
        ...newParentIds(node.id),
      ],
    }));

    return new VirtualNodeGraph(this.originalGraph, [...nodes, ...nodesToAdd]);
  }
}

/**
 * Create a VirtualNodeGraph from a StageFlowNodeGraph or StageRunGraph of existing nodes.
 */
export function createVirtualNodeGraph<T extends StageFlowNodeGraph | StageRunGraph>(
  graph: T
): VirtualNodeGraph<T> {
  const nodes = graph.getNodeIds().map((nodeId) => ({
    id: nodeId,
    parentIds: graph.getParentNodeIds(nodeId),
    stageIndex: graph.getTag(nodeId),
  }));
  return new VirtualNodeGraph(graph, nodes);
}
